import { useContext, useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import styled from "styled-components";
import {
  DropDownBoxWithIcon,
  FullButton,
  MiniAmountBoxFull,
  TextBox,
} from "../styled/input/Input";
import { context } from "../context/context";
import Toast from "../hooks/Toast";
import { formatter } from "../utils/utils";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import CircularLoader from "../styled/loaders/CircularLoader";

const assets = [
  "BTC",
  "ETH",
  "USD",
  "SOL",
  "BCH",
  "LTC",
  "DOGE",
  "USDT",
  "MATIC",
  "AVAX",
  "USDC",
  "AAVE",
  "ALGO",
  "ANC",
  "APE",
  "AURORA",
  "AXS",
  "BTG",
  "BORING",
  "ADA",
  "XCN",
  "LINK",
  "CRO",
  "DAI",
  "DASH",
  "MANA",
  "ETC",
  "EVMOS",
  "GT",
  "LN",
  "XMR",
  "NEXO",
  "OKB",
  "OP",
  "OGN",
  "ORN",
  "DOT",
  "XPR",
  "RARI",
  "SFP",
  "SHIB",
  "XLM",
  "GMT",
  "SUSHI",
  "TLOS",
  "XTZ",
  "GRT",
  "TRX",
  "UNI",
  "VET",
  "WING",
  "ZEC",
  "XRP",
];

const EditDepositModal = ({ open, details }) => {
  const { editDeposit, setEditDeposit } = open;
  const { currentPrices } = useContext(context);

  const [amount, setAmount] = useState("");
  const [selectedAsset, setSelectedAsset] = useState("BTC");
  const [totalInUSD, setTotalInUSD] = useState("");
  const [status, setStatus] = useState("pending");
  const [manualTotal, setManualTotal] = useState(false);

  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(" ");
  const [toastType, setToastType] = useState(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (details) {
      setAmount(details.amount?.toString() ?? "");
      setSelectedAsset(details.asset ?? "BTC");
      setTotalInUSD(details.totalInUSD?.toString() ?? "");
      setStatus(details.status ?? "pending");
      setManualTotal(true);
    }
  }, [details]);

  useEffect(() => {
    if (!manualTotal && amount && selectedAsset && currentPrices?.[selectedAsset]) {
      setTotalInUSD(
        String(Number(amount) * currentPrices[selectedAsset])
      );
    }
  }, [amount, selectedAsset, currentPrices, manualTotal]);

  function handleAmount(e) {
    const { value } = e.target;
    setAmount(value);
    setManualTotal(false);
  }

  function handleAsset(e) {
    setSelectedAsset(e.target.value);
    setManualTotal(false);
  }

  function handleTotalInUSD(e) {
    setTotalInUSD(e.target.value);
    setManualTotal(true);
  }

  async function handleSave() {
    if (!details?.ref || !amount || !selectedAsset || !totalInUSD) {
      return;
    }

    setIsSaving(true);

    await updateDoc(doc(db, "deposits", details.ref), {
      amount: Number(amount),
      asset: selectedAsset,
      totalInUSD: Number(totalInUSD),
      status,
    })
      .then(() => {
        setIsSaving(false);
        setToastType("success");
        setToastMessage("Deposit updated successfully");
        setOpenToast(true);
        setTimeout(() => {
          setEditDeposit(false);
        }, 400);
      })
      .catch(() => {
        setIsSaving(false);
        setToastType("error");
        setToastMessage("Failed to update deposit. Please try again later.");
        setOpenToast(true);
      });
  }

  return (
    <>
      {openToast && (
        <Toast
          open={{ openToast, setOpenToast }}
          message={toastMessage}
          type={toastType}
        />
      )}

      <Modal
        open={editDeposit}
        onClose={() => setEditDeposit(false)}
        style={{
          display: "flex",
          placeContent: "center",
          zIndex: "10001",
          padding: "12px",
        }}
      >
        <ModalStandard className="scrollbar-hide">
          <div className="modal_top">
            <p>Manage deposit</p>

            <svg
              width="15"
              height="14"
              viewBox="0 0 15 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => setEditDeposit(false)}
              style={{ cursor: "pointer" }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.8647 0.366365C12.3532 -0.122122 13.1451 -0.122122 13.6336 0.366365C14.1221 0.854853 14.1221 1.64685 13.6336 2.13533L8.88929 6.87968L13.8743 11.8647C14.3628 12.3532 14.3628 13.1451 13.8743 13.6336C13.3858 14.1221 12.5938 14.1221 12.1053 13.6336L7.12032 8.64864L2.13533 13.6336C1.64685 14.1221 0.854853 14.1221 0.366366 13.6336C-0.122122 13.1451 -0.122122 12.3532 0.366366 11.8647L5.35136 6.87968L0.607014 2.13533C0.118527 1.64685 0.118527 0.854853 0.607014 0.366365C1.0955 -0.122122 1.8875 -0.122122 2.37598 0.366365L7.12032 5.11071L11.8647 0.366365Z"
                fill="#858DAD"
              />
            </svg>
          </div>

          <div className="modal_content">
            <div className="top">
              <TextBox className="scrollbar-hide">
                <label htmlFor="ref">Ref:</label>
                <br />
                <input type="text" value={details?.ref ?? ""} disabled />
              </TextBox>

              <TextBox className="scrollbar-hide">
                <label htmlFor="user">User:</label>
                <br />
                <input
                  type="text"
                  value={details?.userRef?.name ?? ""}
                  disabled
                />
              </TextBox>

              <DropDownBoxWithIcon className="type_select">
                <div className="wrapper">
                  <p className="label">Asset:</p>
                  <span className="content">
                    <div className="icon_wrap">
                      <img src={`./asseticons/${selectedAsset}.svg`} alt="" />
                      <select
                        name="assets"
                        value={selectedAsset}
                        onChange={handleAsset}
                      >
                        {assets.map((curr) => (
                          <option value={curr} key={curr}>
                            {curr}
                          </option>
                        ))}
                      </select>
                    </div>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="#5C6175"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </DropDownBoxWithIcon>

              <MiniAmountBoxFull className="amount_box variant">
                <div className="label">
                  <p>Amount:</p>
                </div>

                <div className="wrapper">
                  <input
                    type="number"
                    placeholder="1000"
                    value={amount}
                    onChange={handleAmount}
                  />

                  <span className="asset">
                    <p>{selectedAsset}</p>
                  </span>
                </div>

                {!manualTotal &&
                  amount &&
                  selectedAsset &&
                  currentPrices?.[selectedAsset] && (
                    <div className="captions">
                      <span>
                        <p className="caption">Suggested total in USD</p>
                        <p className="value" style={{ color: "#5BDE4C" }}>
                          {formatter.format(
                            currentPrices[selectedAsset] * Number(amount)
                          )}
                        </p>
                      </span>
                    </div>
                  )}
              </MiniAmountBoxFull>

              <MiniAmountBoxFull className="amount_box variant">
                <div className="label">
                  <p>Total in USD:</p>
                </div>

                <div className="wrapper">
                  <input
                    type="number"
                    placeholder="1000"
                    value={totalInUSD}
                    onChange={handleTotalInUSD}
                  />

                  <span className="asset">
                    <p>USD</p>
                  </span>
                </div>
              </MiniAmountBoxFull>

              <DropDownBoxWithIcon className="type_select">
                <div className="wrapper">
                  <p className="label">Status:</p>
                  <span className="content">
                    <div className="icon_wrap">
                      <select value={status} onChange={(e) => setStatus(e.target.value)}>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="declined">Declined</option>
                      </select>
                    </div>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="#5C6175"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </DropDownBoxWithIcon>
            </div>

            <div className="bottom">
              <FullButton
                onClick={handleSave}
                disabled={!amount || !selectedAsset || !totalInUSD || isSaving}
                className={
                  (!amount || !selectedAsset || !totalInUSD || isSaving) &&
                  "disabled"
                }
              >
                {isSaving ? (
                  <div style={{ padding: "8px" }}>
                    <CircularLoader bg="#cccccc" size="28" color="#ffffff" />
                  </div>
                ) : (
                  <p>Save</p>
                )}
              </FullButton>
            </div>
          </div>
        </ModalStandard>
      </Modal>
    </>
  );
};

const ModalStandard = styled.div`
  background-color: #151823;
  border-radius: 12px;
  max-width: 430px;
  place-self: center;
  width: 100%;
  border: 1px solid transparent;
  z-index: 10001;

  .bottom {
    margin-top: 32px;
  }

  .modal_top {
    color: white;
    font-size: 16px;
    font-weight: 600;
    background-color: #1b1f2d;
    width: 100%;
    padding: 14px 30px;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    position: sticky;
    top: 0;
    z-index: 999;
    left: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal_content {
    padding: 24px;
  }

  .modal_content .top {
    display: grid;
    gap: 24px;
  }
`;

export default EditDepositModal;
